// src/lib/utils/form-values.ts
//
// Small, framework-free guards + safe extractors for the `unknown`-typed values
// that the resource generators carry in their `Record<string, unknown>` form
// state. These previously lived (re-defined) inside ColorPaletteGenerator.tsx
// (A11/C1 "D1" dedup); harvested here they are shared and unit-tested so every
// generator narrows form values the same way.

/** Narrow an unknown to `string`. */
export const isString = (value: unknown): value is string => typeof value === 'string'

/** Narrow an unknown to `number`. */
export const isNumber = (value: unknown): value is number => typeof value === 'number'

/** Narrow an unknown to `boolean`. */
export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean'

/** Narrow an unknown to `string[]` (every element a string). */
export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string')

/** Read a `string`, falling back to `defaultValue` (default `''`) when not a string. */
export const getString = (value: unknown, defaultValue = ''): string =>
  isString(value) ? value : defaultValue

/** Read a `number`, falling back to `defaultValue` (default `0`) when not a number. */
export const getNumber = (value: unknown, defaultValue = 0): number =>
  isNumber(value) ? value : defaultValue

/** Read a `boolean`, falling back to `defaultValue` (default `false`) when not a boolean. */
export const getBoolean = (value: unknown, defaultValue = false): boolean =>
  isBoolean(value) ? value : defaultValue

/** Read a `string[]`, falling back to `defaultValue` (default `[]`) when not a string array. */
export const getStringArray = (value: unknown, defaultValue: string[] = []): string[] =>
  isStringArray(value) ? value : defaultValue
